import React, { Dispatch } from 'react';
import { TabMenuItemGeneral, TabText } from '../../TabMenuItem/TabMenuItem';
import { TabBarDispatches } from '../../../hooks/tabMenuReducer/tabMenuReducer';
import EventInformation from '../../EventInformation/EventInformation';
import EventSelectionWrapper from '../../EventSelectionWrapper/EventSelectionWrapper';
import EntryStateText from '../../EntryStateText/EntryStateText';
import EntryPicksWrapper from '../../EntryPicksWrapper/EntryPicksWrapper';
import { CarouselItem } from '../../Carousel/Carousel';
import { TFunction } from 'i18next';
import { numberUtilities } from '@incentivegames/ig-frontend-common/lib/utilities/numberUtilities';
import { Event } from '../../../interfaces/sports';

export const eventNumbers = [1, 2, 3, 4, 5, 6];

const getNextTab = ({
  current,
  updatedEvent,
  items,
}: {
  current: Exclude<TabText, 'payout-table'>;
  updatedEvent: boolean;
  items: TabMenuItemGeneral[];
}) => {
  if ((updatedEvent && current !== 'my-selections') || current === eventNumbers.length) return 'my-selections';
  const firstUnselectedEvent = items.find(
    (item) => !item.selectionMade && typeof item.tabText === 'number' && item.tabText !== current,
  );
  return typeof current === 'number' && firstUnselectedEvent ? firstUnselectedEvent.tabText : 'my-selections';
};

export const getEventWrappers = ({
  eventNumbers,
  setTab,
  updatedEvent,
  setUpdatedEvent,
  items,
}: {
  eventNumbers: number[];
  setTab: TabBarDispatches['setTab'];
  updatedEvent: boolean;
  setUpdatedEvent: Dispatch<number | undefined>;
  items: TabMenuItemGeneral[];
}): CarouselItem[] => {
  return eventNumbers.map((eventNumber) => ({
    id: eventNumber,
    node: (
      <>
        <EventInformation eventNumber={eventNumber} />
        <div className={'selection-screen__card-wrapper'}>
          <EventSelectionWrapper
            eventNumber={eventNumber}
            onClick={() => {
              setTab({
                item: getNextTab({
                  current: eventNumber,
                  updatedEvent: updatedEvent,
                  items,
                }),
                selectionMade: true,
              });
              if (updatedEvent) setUpdatedEvent(eventNumber);
            }}
          />
        </div>
      </>
    ),
  }));
};

export const getMySelectionScreen = ({
  setUpdatedEvent,
  setTab,
  updatedEvent,
  t,
}: {
  setUpdatedEvent: Dispatch<number | undefined>;
  setTab: TabBarDispatches['setTab'];
  updatedEvent: number | undefined;
  t: TFunction;
}): CarouselItem => {
  const uniqueId = numberUtilities.getUniqueId();

  return {
    id: 'my-selections',
    node: (
      <>
        <EntryStateText />
        <div className={'selection-screen__entry-wrapper'}>
          <div className={'controls-info'}>
            <div className={'controls-info__side'}>{t('my-selections')}</div>
            <div className={'controls-info__side controls-info__side--right'}>{t('edit')}</div>
          </div>
          <EntryPicksWrapper
            uniqueId={uniqueId}
            state={'pre-play'}
            onClick={(eventNumber) => {
              setTab({ item: eventNumber, selectionMade: false });
              setUpdatedEvent(undefined);
            }}
            updateEventNumber={updatedEvent}
          />
        </div>
      </>
    ),
  };
};

/**
 * Handles the business logic around checking whether the user
 * can navigate to the tab they have klicked.
 * @returns Information about the tab if the user is allowed
 * to navigate to it, otherwise null.
 */
export function getTargetTab({
  targetTab,
  current,
  eventNumbers,
  items,
  currentEvent,
}: {
  targetTab: TabText;
  items: TabMenuItemGeneral[];
  current: TabText;
  eventNumbers: number[];
  currentEvent: Event | undefined;
}): { item: TabText; currentTabSelectionMade: boolean } | null {
  if (targetTab === current) return null;
  const currentItem = items.find((item) => item.tabText === current);
  const targetItem = items.find((item) => item.tabText === targetTab);

  const currentTabSelectionMade = (!currentEvent?.eventActive || currentItem?.selectionMade) ?? false;

  // All of the tab items which have a numeric tabText are the players picks
  // Other tab items (such as My Selections) are different.
  const picks = items.filter((i) => typeof i.tabText === 'number');

  const selectionsMade = picks.filter(
    (pick) => pick.selectionMade || (pick.tabText === current && !currentEvent?.eventActive),
  );

  // You can click on any of the picks which already have selections made,
  // or the next pick which does not yet have a selection made.
  const clickableRaces = selectionsMade.length + 1;
  if (typeof targetItem?.tabText === 'number' && targetItem?.tabText > clickableRaces) return null;

  // if you try to select my-selections before all selections have been made, do nothing
  const allSelectionsMade = selectionsMade.length === eventNumbers.length;
  if (targetItem?.tabText === 'my-selections' && !allSelectionsMade) return null;

  return { currentTabSelectionMade, item: targetTab };
}
